import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  InfiniteScroll,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { PiFunnel, PiFunnelThin } from 'react-icons/pi';
import { XIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { SimpleFilter, useFilterContext } from '../FilterContext';
import { Filter, useGetFilters } from '@ais-ui/websockets';
import { CheckIcon } from '@radix-ui/react-icons';
import { cn } from '@ais-ui/utils';

const ExistingFilter = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const filterContext = useFilterContext();
  const { data: filters, isLoading } = useGetFilters();
  const [search, setSearch] = useState('');
  const handleSelect = (filter: Filter) => {
    // Check if the uuid already exists in customFilters
    const uuidExists = filterContext.customFilters.some(
      (existingFilter) => existingFilter.uuid === filter.uuid,
    );

    if (!uuidExists) {
      // If the uuid doesn't exist, add the new filter
      filterContext.setCustomFilters([...filterContext.customFilters, filter]);
    } else {
      filterContext.setCustomFilters(
        filterContext.customFilters.filter(
          (existingFilter) => existingFilter.uuid !== filter.uuid,
        ),
      );
    }
  };

  const createSafeValue = (filter: Filter) => {
    // Remove or replace problematic characters, otherwise we'll crash the application for example for:
    // evi_classification_ = "DET NORSKE"
    const safeName = filter.name.replace(/[^a-zA-Z0-9\-_]/g, '_');
    return `${safeName}|${filter.uuid}`;
  };

  return (
    <Command className="h-fit min-h-96 mt-2">
      <CommandInput
        placeholder="Search for filter..."
        className="h-9"
        onValueChange={(val) => setSearch(val)}
      />
      {!isLoading && (
        <CommandEmpty>{messages.mapMenuFilterNoFiltersFound}</CommandEmpty>
      )}
      <CommandList>
        <CommandGroup heading="Apply filters">
          {isLoading && <LoadingSpinner size="small" />}
          {!isLoading && (
            <InfiniteScroll
              items={filters}
              limit={10}
              search={search}
              observeResize={true}
              filterFn={(item, search) =>
                item.name.toLowerCase().includes(search.toLowerCase())
              }
              sortFn={(a, b) => a.name.localeCompare(b.name)}
              renderItem={(filter: Filter) => {
                return (
                  <CommandItem
                    key={filter.uuid}
                    value={createSafeValue(filter)}
                    onSelect={() => handleSelect(filter)}
                  >
                    <div className="flex justify-between w-full">
                      <div>{filter.name}</div>
                      {filterContext.customFilters.some(
                        (customFilter) => customFilter.uuid === filter.uuid,
                      ) && <CheckIcon className="size-4" />}
                    </div>
                  </CommandItem>
                );
              }}
              className="h-65 max-h-65 h-min-65 pr-2 pt-2 overflow-y-auto"
            ></InfiniteScroll>
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

interface FilterBadgeProps {
  filter: Filter | SimpleFilter;
  className?: string;
}

export const FilterBadge = ({ filter, className }: FilterBadgeProps) => {
  const filterContext = useFilterContext();
  const onBadgeClick = (filter: Filter | SimpleFilter) => {
    const isFilter = (filter: Filter | SimpleFilter): filter is Filter => {
      return (filter as Filter).uuid !== undefined;
    };

    if (isFilter(filter)) {
      const newFilters = filterContext.customFilters.filter(
        (item) => item.uuid !== filter.uuid,
      );
      filterContext.setCustomFilters(newFilters);
    } else {
      const newFilters = filterContext.simpleFilters.filter(
        (item) => item.id !== filter.id,
      );
      filterContext.setSimpleFilters(newFilters);
    }
  };

  return (
    <div
      key={filter.name}
      className={cn('relative group px-1 py-0.5', className)}
    >
      <Badge
        className="space-x-2 hover:cursor-pointer"
        onClick={() => onBadgeClick(filter)}
      >
        <p>{filter.name}</p>
        <XIcon className="size-4" />
      </Badge>
    </div>
  );
};

interface FilterBadgesProps {
  allFilters: (Filter | SimpleFilter)[];
}

const FilterBadges = ({ allFilters }: FilterBadgesProps) => {
  return (
    <div className="flex pointer-events-auto ml-1">
      {allFilters.map((filter) => {
        const isFilter = (filter: Filter | SimpleFilter): filter is Filter => {
          return (filter as Filter).uuid !== undefined;
        };

        // Use the type guard to check the type of filter
        const key = isFilter(filter) ? filter.uuid : filter.id;

        return <FilterBadge key={key} filter={filter} />;
      })}
    </div>
  );
};

const ActiveFilters = () => {
  const { messages } = useLocalization();
  const filterContext = useFilterContext();
  const allFilters = [
    ...filterContext.simpleFilters,
    ...filterContext.customFilters,
  ];

  return (
    <Command>
      <CommandList className="overflow-hidden h-full">
        <CommandGroup heading="Active filters">
          {allFilters.length === 0 && (
            <div className="py-2 text-center text-sm">{messages.mapMenuFilterNoFiltersApplied}</div>
          )}
          {allFilters.length >= 1 && (
            <div className="flex flex-wrap pb-2">
              {filterContext.customFilters.map((filter) => (
                <FilterBadge key={filter.uuid} filter={filter}></FilterBadge>
              ))}
              {filterContext.simpleFilters.map((filter) => (
                <div key={filter.name} className="relative group px-1 py-0.5">
                  <Badge className="space-x-2">
                    <p>{filter.name}</p>
                    <XIcon className="size-4" />
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

interface FilterShipsProps {
  className?: string;
}

export const FilterShips = React.memo(({ className }: FilterShipsProps) => {
  const { messages } = useLocalization();
  const [open, setOpen] = React.useState(false);
  const filterContext = useFilterContext();

  const allFilters = useMemo(
    () => [...filterContext.simpleFilters, ...filterContext.customFilters],
    [filterContext.simpleFilters, filterContext.customFilters],
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                id="filter-tool-button"
                size="icon"
                variant="outline"
                onClick={() => setOpen((prev) => !prev)}
                className={cn(
                  'flex items-center justify-center size-8 p-1 group/btn rounded-lg',
                  className,
                )}
              >
                <div className="relative">
                  <PiFunnel className="size-5 group-hover/btn:text-primary" />
                  {allFilters.length > 0 && (
                    <span className="absolute -top-2 -right-2.5 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-red-100 bg-primary rounded-full">
                      {allFilters.length}
                    </span>
                  )}
                </div>
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent>{messages.mapFilterByButtonTooltip}</TooltipContent>
          <PopoverContent side="right" align="end">
            <ActiveFilters />
            <ExistingFilter />
          </PopoverContent>
        </Popover>
      </Tooltip>
    </TooltipProvider>
  );
});
